import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  afterNextRender,
  computed,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatFormField, MatHint, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { ConfirmDialogComponent } from '../../../components/confirm-dialog/confirm-dialog.component';
import { RunnerTargetSelectComponent } from '../../../components/runner-target-select/runner-target-select.component';
import { ObjectMappingService } from '../../../services/object-mapping.service';
import { ObjectCaptureService, type RecordingStart } from '../../../services/object-capture.service';
import {
  RECORD_ENGINES,
  isRecordEngine,
  type RecordEngine,
  type RunTarget,
} from '../../../services/execution-job.service';
import { CreateObjectMappingRequest, ObjectMappingSummary } from '../../../models/object-mapping.model';
import { FIELD_LENGTHS } from '../../../shared/field-lengths';
import { isValidObjectName } from '../object-names';
import { isNameTaken, serverMessage } from '../../../shared/http/server-message';

export type CreateWay = 'empty' | 'record' | 'tox';

const RECORD_ERRORS: Readonly<Record<string, string>> = {
  RUNNER_OFFLINE: 'objectMappings.record.errors.runnerOffline',
  RUNNER_CANNOT_RECORD: 'objectMappings.record.errors.cannotRecord',
  RECORDING_ACTIVE: 'objectMappings.record.errors.recordingActive',
  ENGINE_NOT_SUPPORTED: 'objectMappings.record.errors.engineNotSupported',
};

function isHttpUrl(value: string): boolean {
  try {
    const { protocol } = new URL(value);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Testelement anlegen (NEX-1705, NEX-2447): leer, per Aufnahme oder Hinweis auf den Classic-Import
 * (NEX-1719), Design `page-abgleich-bloecke.jsx:405-482`. Mit `library` ist es der Dialog
 * „Aufnehmen“ eines vorhandenen Testelements: ohne Name und ohne Arten.
 *
 * Die Technologie (NEX-2066) bestimmt den Start: Web mit Start-Adresse, Desktop mit optionalem
 * Programm, SAP ohne Feld. Bei Desktop und SAP scannt das System das Fenster im Vordergrund.
 *
 * Name und Adresse sind native Felder wie im Design: `matInput` setzt `aria-invalid` selbst und
 * liesse es bei einem leeren Pflichtfeld weg.
 */
@Component({
  selector: 'app-object-mapping-create-dialog',
  imports: [
    TranslatePipe,
    RouterLink,
    MatFormField,
    MatLabel,
    MatHint,
    MatInput,
    MatButton,
    MatIcon,
    ConfirmDialogComponent,
    RunnerTargetSelectComponent,
  ],
  templateUrl: './object-mapping-create-dialog.component.html',
  styleUrl: './object-mapping-create-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ObjectMappingCreateDialogComponent {
  private readonly service = inject(ObjectMappingService);
  private readonly captures = inject(ObjectCaptureService);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  readonly projectId = input.required<string>();
  /** Gesetzt: Aufnahme fuer diese Bibliothek statt einer neuen. */
  readonly library = input<ObjectMappingSummary | null>(null);
  /** Namen aller Testelemente des Projekts; ein gleicher Name ohne Gross-/Kleinschreibung ist vergeben. */
  readonly existingNames = input<readonly string[]>([]);
  readonly created = output<ObjectMappingSummary>();
  readonly cancelled = output<void>();

  readonly fieldLengths = FIELD_LENGTHS;
  readonly ways: readonly { id: CreateWay; icon: string }[] = [
    { id: 'empty', icon: 'add' },
    { id: 'record', icon: 'radio_button_checked' },
    { id: 'tox', icon: 'upload_file' },
  ];

  readonly engines = RECORD_ENGINES;

  readonly name = signal('');
  readonly nameTouched = signal(false);
  readonly address = signal('');
  readonly chosenEngine = signal<RecordEngine>('browser');
  readonly way = signal<CreateWay>('empty');
  readonly target = signal<RunTarget | null>(null);
  readonly startProgram = signal('');
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);
  /** Laufende Aufnahme, wenn der Start an RECORDING_ACTIVE scheiterte. */
  readonly activeJobId = signal<string | null>(null);
  /**
   * Die schon angelegte Bibliothek, wenn danach der Start scheiterte. Ein neuer Versuch legt sie
   * nicht noch einmal an, er startet nur die Aufnahme.
   */
  readonly createdLibrary = signal<ObjectMappingSummary | null>(null);

  readonly recordOnly = computed(() => this.library() !== null);
  readonly recording = computed(() => this.recordOnly() || this.way() === 'record');
  readonly mapping = computed(() => this.library() ?? this.createdLibrary());
  readonly locked = computed(() => this.createdLibrary() !== null);
  /** Im Dialog „Aufnehmen“ gibt die Bibliothek die Engine vor. */
  readonly engine = computed<RecordEngine>(() => {
    const engineId = this.library()?.engineId;
    if (engineId === undefined) return this.chosenEngine();
    return isRecordEngine(engineId) ? engineId : 'browser';
  });
  readonly scanning = computed(() => this.engine() !== 'browser');
  /** Im Dialog „Aufnehmen“ einer Web-Bibliothek steht ihre Adresse schon als Startadresse da (Bundle). */
  private readonly presetUrl = computed(() =>
    this.recordOnly() && this.engine() === 'browser' ? (this.library()?.address ?? '') : '',
  );
  /** Die Detailseite setzt `library` nach jedem Speichern neu; eine getippte Adresse bleibt trotzdem stehen. */
  private urlEdited = false;
  readonly startUrl = linkedSignal<string, string>({
    source: this.presetUrl,
    computation: (preset, previous) => (this.urlEdited && previous ? previous.value : preset),
  });

  /** Schluessel der Meldung zum Namen, `null` ohne Fehler; Reihenfolge wie im Design. */
  readonly nameError = computed<string | null>(() => {
    const name = this.name();
    const trimmed = name.trim();
    if (!trimmed) return 'objectMappings.create.nameRequired';
    if (/[\s.]/.test(name)) return 'objectMappings.create.nameFormat';
    if (!isValidObjectName(name)) return 'objectMappings.create.invalidName';
    const lower = trimmed.toLowerCase();
    if (this.existingNames().some((existing) => existing.toLowerCase() === lower)) {
      return 'objectMappings.create.nameTaken';
    }
    return null;
  });
  readonly nameValid = computed(() => this.nameError() === null);
  readonly showNameError = computed(() => this.nameTouched() && !this.locked() && !this.nameValid());
  readonly urlValid = computed(() => isHttpUrl(this.startUrl()));
  readonly showUrlInvalid = computed(() => this.startUrl().length > 0 && !this.urlValid());

  readonly canSubmit = computed(() => {
    if (this.saving()) return false;
    if (!this.recordOnly() && this.way() === 'tox') return true;
    const nameOk = this.mapping() !== null || this.nameValid();
    if (!this.recording()) return nameOk;
    const startOk = this.scanning() || this.urlValid();
    return nameOk && this.target()?.kind === 'runner' && startOk;
  });

  readonly title = computed(() => (this.recordOnly() ? 'objectMappings.record.title' : 'objectMappings.create.title'));
  readonly message = computed(() =>
    this.recordOnly() ? 'objectMappings.record.message' : 'objectMappings.create.message',
  );
  readonly startUrlLabel = computed(() =>
    this.recordOnly() ? 'objectMappings.record.startAddress' : 'objectMappings.record.startUrl',
  );
  readonly submitLabel = computed(() => {
    if (this.recordOnly()) return 'objectMappings.record.submit';
    if (this.way() === 'record') return 'objectMappings.create.submitRecord';
    return this.way() === 'tox' ? 'objectMappings.create.ways.tox.close' : 'objectMappings.create.submit';
  });

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly nameInput = viewChild<ElementRef<HTMLInputElement>>('nameInput');
  private readonly urlInput = viewChild<ElementRef<HTMLInputElement>>('urlInput');
  private readonly runnerSelect = viewChild(RunnerTargetSelectComponent);

  constructor() {
    afterNextRender(() => (this.nameInput() ?? this.urlInput())?.nativeElement.focus());
  }

  onNameInput(event: Event): void {
    this.name.set((event.target as HTMLInputElement).value);
    this.error.set(null);
  }

  onAddressInput(event: Event): void {
    this.address.set((event.target as HTMLInputElement).value);
  }

  onUrlInput(event: Event): void {
    this.urlEdited = true;
    this.startUrl.set((event.target as HTMLInputElement).value);
  }

  onProgramInput(event: Event): void {
    this.startProgram.set((event.target as HTMLInputElement).value);
  }

  /** Startadresse und Programm gehoeren zur Engine; nach dem Wechsel passen sie nicht mehr. */
  chooseEngine(engine: RecordEngine): void {
    this.chosenEngine.set(engine);
    this.urlEdited = false;
    this.startUrl.set('');
    this.startProgram.set('');
    this.error.set(null);
  }

  /** Bei Windows und SAP heisst der Weg „Aufnehmen“ „Fenster scannen“. */
  wayKey(way: CreateWay): string {
    return way === 'record' && this.scanning() ? 'scan' : way;
  }

  chooseWay(way: CreateWay): void {
    if (this.locked()) return;
    this.way.set(way);
    this.error.set(null);
  }

  /** Pfeiltasten wandern und waehlen, Leertaste und Enter waehlen (Muster `runner-radio-list`). */
  onWayKeydown(event: KeyboardEvent, way: CreateWay): void {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      this.chooseWay(way);
      return;
    }
    const step = ({ ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 } as Record<string, number>)[event.key];
    if (step === undefined || this.locked()) return;
    event.preventDefault();
    const ids = this.ways.map((option) => option.id);
    const next = ids[(ids.indexOf(way) + step + ids.length) % ids.length];
    this.chooseWay(next);
    this.host.nativeElement.querySelector<HTMLElement>(`[data-testid="om-create-way-${next}"]`)?.focus();
  }

  submit(): void {
    if (!this.canSubmit()) return;
    if (!this.recordOnly() && this.way() === 'tox') {
      this.cancelled.emit();
      return;
    }
    const mapping = this.mapping();
    if (mapping !== null) {
      this.startRecording(mapping);
      return;
    }
    this.create();
  }

  /** Nach dem Anlegen ist die Liste ohne die neue Bibliothek veraltet; deshalb auch hier `created`. */
  cancel(): void {
    const created = this.createdLibrary();
    if (created === null) this.cancelled.emit();
    else this.created.emit(created);
  }

  /** Die Detailseite oeffnet nur dieser Knopf; nach „Abbrechen“ bleibt die Liste offen. */
  toLibrary(): void {
    const library = this.createdLibrary()!;
    void this.router.navigate(['/projects', this.projectId(), 'object-mappings', library.id]);
    this.created.emit(library);
  }

  private create(): void {
    this.saving.set(true);
    this.error.set(null);
    this.service
      .create(this.projectId(), this.createRequest())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (library) => {
          if (!this.recording()) {
            this.saving.set(false);
            this.created.emit(library);
            return;
          }
          this.createdLibrary.set(library);
          this.startRecording(library);
        },
        error: (err: unknown) => {
          this.error.set(this.createMessage(err));
          this.saving.set(false);
        },
      });
  }

  private startRecording(mapping: ObjectMappingSummary): void {
    const target = this.target();
    if (target?.kind !== 'runner') {
      this.saving.set(false);
      this.error.set(this.translate.instant('objectMappings.record.errors.chooseRunner'));
      return;
    }
    this.saving.set(true);
    this.error.set(null);
    this.activeJobId.set(null);
    this.captures
      .startRecording(this.projectId(), mapping.id, target.runnerId, this.recordingStart())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (job) => {
          this.saving.set(false);
          void this.router.navigate([
            '/projects',
            this.projectId(),
            'object-mappings',
            mapping.id,
            'recording',
            job.id,
          ]);
        },
        error: (err: unknown) => {
          this.saving.set(false);
          this.showStartError(err);
        },
      });
  }

  /** Web bleibt ohne `engineId` (das BE nimmt dann browser), eine leere Adresse faellt weg. */
  private createRequest(): CreateObjectMappingRequest {
    const engine = this.chosenEngine();
    const address = this.address().trim();
    return {
      name: this.name(),
      ...(engine === 'browser' ? {} : { engineId: engine }),
      ...(address ? { address } : {}),
    };
  }

  private recordingStart(): RecordingStart {
    switch (this.engine()) {
      case 'browser':
        return { startUrl: this.startUrl() };
      case 'windows': {
        const program = this.startProgram().trim();
        return program === '' ? {} : { startProgram: program };
      }
      case 'sap':
        return {};
    }
  }

  private showStartError(err: unknown): void {
    const body = err instanceof HttpErrorResponse ? (err.error as { code?: unknown; jobId?: unknown } | null) : null;
    const key = typeof body?.code === 'string' ? RECORD_ERRORS[body.code] : undefined;
    if (key) {
      this.error.set(this.translate.instant(key));
      if (body?.code === 'RECORDING_ACTIVE' && typeof body.jobId === 'string') this.activeJobId.set(body.jobId);
      return;
    }
    if (err instanceof HttpErrorResponse && err.status === 404) {
      this.error.set(this.translate.instant('objectMappings.record.errors.runnerGone'));
      this.runnerSelect()?.loadRunners();
      return;
    }
    this.error.set(serverMessage(err) ?? this.translate.instant('objectMappings.record.errors.general'));
  }

  private createMessage(err: unknown): string {
    if (isNameTaken(err)) return this.translate.instant('objectMappings.create.nameTaken');
    return serverMessage(err) ?? this.translate.instant('objectMappings.create.error');
  }
}
